import type React from "react";
import { useState, useEffect } from "react";
import { Button, Select, Typography, Space, Row, Col, Card } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { styled } from "@stitches/configed";
import { DayGroup, TimeSlot, BusinessHoursProps } from "../data";

const { Text } = Typography;
const { Option } = Select;

const CustomButton = styled(Button, {
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none !important',
  outline: 'none',
  padding: '10px 12px',
  marginTop: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& > span': {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '18px',
    letterSpacing: '0px',
  },

  '&:not(:disabled) span': {
    color: '#0D9A62',
  },
});

const BinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.66659 1.43685V0.103516H10.3333V1.43685H13.6666V2.77018H12.3333V12.7702C12.3333 12.947 12.263 13.1166 12.138 13.2416C12.013 13.3666 11.8434 13.4368 11.6666 13.4368H2.33325C2.15644 13.4368 1.98687 13.3666 1.86185 13.2416C1.73682 13.1166 1.66659 12.947 1.66659 12.7702V2.77018H0.333252V1.43685H3.66659ZM2.99992 2.77018V12.1035H10.9999V2.77018H2.99992ZM4.99992 4.77018H6.33325V10.1035H4.99992V4.77018ZM7.66659 4.77018H8.99992V10.1035H7.66659V4.77018Z" fill="#D33C0C" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.00016 15.4368C4.31816 15.4368 1.3335 12.4522 1.3335 8.77018C1.3335 5.08818 4.31816 2.10352 8.00016 2.10352C11.6822 2.10352 14.6668 5.08818 14.6668 8.77018C14.6668 12.4522 11.6822 15.4368 8.00016 15.4368ZM8.00016 14.1035C9.41465 14.1035 10.7712 13.5416 11.7714 12.5414C12.7716 11.5412 13.3335 10.1847 13.3335 8.77018C13.3335 7.35569 12.7716 5.99914 11.7714 4.99895C10.7712 3.99875 9.41465 3.43685 8.00016 3.43685C6.58567 3.43685 5.22912 3.99875 4.22893 4.99895C3.22873 5.99914 2.66683 7.35569 2.66683 8.77018C2.66683 10.1847 3.22873 11.5412 4.22893 12.5414C5.22912 13.5416 6.58567 14.1035 8.00016 14.1035ZM8.00016 7.82752L9.8855 5.94152L10.8288 6.88485L8.94283 8.77018L10.8288 10.6555L9.8855 11.5988L8.00016 9.71285L6.11483 11.5988L5.1715 10.6555L7.0575 8.77018L5.1715 6.88485L6.11483 5.94152L8.00016 7.82752Z" fill="#677187" />
  </svg>

);

const CustomDisabledOption = styled(Option, {
  color: 'rgba(0, 0, 0, 0.85)',
  backgroundColor: '#fff',
});

const DayButton = styled(Button, {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '18px',
  letterSpacing: '0px',
  borderRadius: '6px',
  boxShadow: 'none !important',
  outline: 'none',

  '&.active': {
    backgroundColor: '#ffa500',
  },

  '&:disabled': {
    color: '#7D8493',
    background: '#F6F7F8'
  }
});

const CustomCard = styled(Card, {
  transition: 'all 0.1s ease-out',
  '&.removing': {
    opacity: 0,
    transform: 'translateY(-10px)',
  }
});

const CustomRow = styled(Row, {
  transition: 'all 0.1s ease-out',
  '&.removing': {
    opacity: 0,
    transform: 'translateY(-10px)',
  }
});

const WEEKDAYS = {
  "Thứ Hai": "MONDAY",
  "Thứ Ba": "TUESDAY",
  "Thứ Tư": "WEDNESDAY",
  "Thứ Năm": "THURSDAY",
  "Thứ Sáu": "FRIDAY",
  "Thứ Bảy": "SATURDAY",
  "Chủ Nhật": "SUNDAY"
} as const;

const BusinessHours: React.FC<BusinessHoursProps> = ({ onChange, validate, businessHours }) => {
  const [dayGroups, setDayGroups] = useState<DayGroup[]>(businessHours || [
    {
      id: "group1",
      selectedDays: [],
      timeSlots: [{ id: "slot1", start: null, end: null, block: 0 }],
      weekdays: []
    },
  ]);

  const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set());
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (validate) {
      validateGroups(dayGroups);
    }
  }, [validate, dayGroups]);

  useEffect(() => {
    if (businessHours && businessHours.length > 0) setDayGroups(businessHours);
  }, [businessHours]);

  const days = ["Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy", "Chủ Nhật"];

  // Generate time options (hours and minutes)
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const hourStr = hour.toString().padStart(2, "0");
      for (let minute = 0; minute < 60; minute += 15) {
        const minuteStr = minute.toString().padStart(2, "0");
        const time = `${hourStr}:${minuteStr}`;
        options.push({ value: time, label: time });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const formatWorkTimes = (groups: DayGroup[]) => {
    return groups.map((group, index) => ({
      ...group,
      timeSlots: group.timeSlots.map(slot => ({
        id: slot.id,
        start: slot.start,
        end: slot.end,
        isStartWorkTime: slot.start === "open",
        isEndWorkTime: slot.end === "close"
      })),
      weekdays: group.selectedDays.map(day => WEEKDAYS[day as keyof typeof WEEKDAYS]),
      block: index
    }));
  };

  const toggleDay = (groupId: string, day: string) => {
    const dayIndex = days.indexOf(day);
    const newSelectedDays = new Set(selectedDays);

    const newGroups = dayGroups.map((group) => {
      if (group.id === groupId) {
        const selectedDays = [...group.selectedDays];
        if (selectedDays.includes(day)) {
          newSelectedDays.delete(dayIndex);
          return { ...group, selectedDays: selectedDays.filter((d) => d !== day) };
        } else {
          newSelectedDays.add(dayIndex);
          return { ...group, selectedDays: [...selectedDays, day] };
        }
      }
      return group;
    });

    setDayGroups(newGroups);
    setSelectedDays(newSelectedDays);
    onChange?.(formatWorkTimes(newGroups));
  };

  const addTimeSlot = (groupId: string) => {
    setDayGroups(
      dayGroups.map((group) => {
        if (group.id === groupId) {
          const newId = `slot${Date.now()}`;
          return { ...group, timeSlots: [...group.timeSlots, { id: newId, start: null, end: null }] };
        }
        return group;
      }),
    );
  };

  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  const [removingSlotId, setRemovingSlotId] = useState<string | null>(null);

  const removeDayGroup = (groupId: string) => {
    if (dayGroups.length > 1) {
      setRemovingGroupId(groupId);
      // Find the group being removed
      const groupToRemove = dayGroups.find(group => group.id === groupId);
      if (groupToRemove) {
        // Create a new set without the days from the removed group
        const newSelectedDays = new Set(selectedDays);
        groupToRemove.selectedDays.forEach(day => {
          const dayIndex = days.indexOf(day);
          newSelectedDays.delete(dayIndex);
        });
        setSelectedDays(newSelectedDays);
      }
      setTimeout(() => {
        const newGroups = dayGroups.filter((group) => group.id !== groupId);
        setDayGroups(newGroups);
        onChange?.(formatWorkTimes(newGroups));
        setRemovingGroupId(null);
      }, 300);
    }
  };

  const removeTimeSlot = (groupId: string, slotId: string) => {
    setRemovingSlotId(slotId);
    setTimeout(() => {
      setDayGroups(
        dayGroups.map((group) => {
          if (group.id === groupId && group.timeSlots.length > 1) {
            return { ...group, timeSlots: group.timeSlots.filter((slot) => slot.id !== slotId) };
          }
          return group;
        }),
      );
      setRemovingSlotId(null);
    }, 300);
  };

  const isOptionDisabled = (slot: TimeSlot, field: "start" | "end", value: string | null) => {
    if (value === "open" && slot.end === "close") return true;
    if (value === "close" && slot.start === "open") return true;
    return false;
  };

  const updateTimeSlot = (groupId: string, slotId: string, field: "start" | "end", value: string | null) => {
    const newGroups = dayGroups.map((group) => {
      if (group.id === groupId) {
        return {
          ...group,
          timeSlots: group.timeSlots.map((slot) => {
            if (slot.id === slotId) {
              return { ...slot, [field]: value };
            }
            return slot;
          }),
        };
      }
      return group;
    });
    setDayGroups(newGroups);
    onChange?.(formatWorkTimes(newGroups));
  };

  const addDayGroup = () => {
    const newGroupId = `group${Date.now()}`;
    setDayGroups([
      ...dayGroups,
      {
        id: newGroupId,
        selectedDays: [],
        timeSlots: [{ id: `slot${Date.now()}`, start: null, end: null }],
        weekdays: []
      },
    ]);
  };

  const validateGroups = (groups: DayGroup[]) => {
    const newErrors: { [key: string]: string } = {};

    groups.forEach((group, index) => {
      if (group.selectedDays.length === 0) {
        newErrors[`days-${group.id}`] = "Vui lòng chọn ít nhất một ngày";
      }

      // Check for overlapping time slots
      const timeSlots = group.timeSlots.filter(slot => slot.start !== null && slot.end !== null);
      for (let i = 0; i < timeSlots.length; i++) {
        for (let j = i + 1; j < timeSlots.length; j++) {
          const slot1 = timeSlots[i];
          const slot2 = timeSlots[j];

          if (slot1.start && slot1.end && slot2.start && slot2.end) {
            const start1 = new Date(`2000-01-01T${slot1.start}`);
            const end1 = new Date(`2000-01-01T${slot1.end}`);
            const start2 = new Date(`2000-01-01T${slot2.start}`);
            const end2 = new Date(`2000-01-01T${slot2.end}`);

            if ((start1 < end2 && end1 > start2) || (start2 < end1 && end2 > start1)) {
              newErrors[`time-${group.id}`] = "Các khung giờ không được chồng lấn nhau";
              break;
            }
          }
        }
        if (newErrors[`time-${group.id}`]) break;
      }

      const hasValidTimeSlot = group.timeSlots.some(slot => {
        // Check if both start and end are null (invalid combination)
        if (slot.start === null && slot.end === null) {
          newErrors[`time-${group.id}`] = "Vui lòng chọn ít nhất một khung giờ hợp lệ";
          return false;
        }
        return (slot.start !== null || slot.isStartWorkTime) &&
          (slot.end !== null || slot.isEndWorkTime);
      });

      if (!hasValidTimeSlot && !newErrors[`time-${group.id}`]) {
        newErrors[`time-${group.id}`] = "Vui lòng chọn ít nhất một khung giờ hợp lệ";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div >
      {dayGroups.map((group, groupIndex) => (
        <CustomCard
          key={group.id}
          style={{ marginBottom: 16 }}
          className={removingGroupId === group.id ? 'removing' : ''}
        >
          <CustomRow gutter={[8, 16]} align="middle">
            <Col flex="auto">
              <Space size={8} wrap>
                {days.map((day) => (
                  <DayButton
                    key={day}
                    type={group.selectedDays.includes(day) ? "primary" : "default"}
                    className={group.selectedDays.includes(day) ? "active" : ""}
                    onClick={() => toggleDay(group.id, day)}
                    disabled={!group.selectedDays.includes(day) && selectedDays.has(days.indexOf(day))}
                  >
                    {day}
                  </DayButton>
                ))}
              </Space>
              {errors[`days-${group.id}`] && (
                <p style={{ color: '#cf4c22', margin: '8px 0 0', fontSize: 12 }}>
                  {errors[`days-${group.id}`]}
                </p>
              )}
            </Col>

            <Col>
              {
                dayGroups.length > 1 && (
                  <DayButton
                    type="text"
                    icon={<BinIcon />}
                    onClick={() => removeDayGroup(group.id)}
                  />
                )
              }
            </Col>
          </CustomRow>

          <div style={{ marginTop: 16 }}>
            <Text style={{ fontSize: 12 }}>Giờ mở bán</Text>

            {group.timeSlots.map((slot) => (
              <CustomRow
                key={slot.id}
                gutter={[16, 16]}
                style={{ marginTop: 8 }}
                align="middle"
                className={removingSlotId === slot.id ? 'removing' : ''}
              >
                <Col span={11}>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="--"
                    value={slot.start}
                    onChange={(value) => updateTimeSlot(group.id, slot.id, "start", value)}
                    dropdownMatchSelectWidth={false}
                  >
                    <Option disabled value={null}>--</Option>
                    <CustomDisabledOption
                      value="open"
                      disabled={isOptionDisabled(slot, "start", "open")}
                    >
                      Từ giờ mở cửa
                    </CustomDisabledOption>
                    {timeOptions.map(({ value, label }) => (
                      <Option key={`start-${value}`} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={1} style={{ textAlign: "center" }}>
                  -
                </Col>
                <Col span={10}>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="--"
                    value={slot.end}
                    onChange={(value) => updateTimeSlot(group.id, slot.id, "end", value)}
                    dropdownMatchSelectWidth={false}
                  >
                    <Option disabled value={null}>--</Option>
                    <CustomDisabledOption
                      value="close"
                      disabled={isOptionDisabled(slot, "end", "close")}
                    >
                      Đến giờ đóng cửa
                    </CustomDisabledOption>
                    {timeOptions.map(({ value, label }) => (
                      <Option key={`end-${value}`} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={2}>
                  <Button
                    type="text"
                    icon={<XIcon />}
                    onClick={() => removeTimeSlot(group.id, slot.id)}
                    disabled={group.timeSlots.length === 1}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', boxShadow: 'none' }}
                  />
                </Col>
              </CustomRow>
            ))}

            {errors[`time-${group.id}`] && (
              <p style={{ color: '#cf4c22', margin: '8px 0 0', fontSize: 12 }}>
                {errors[`time-${group.id}`]}
              </p>
            )}
          </div>

          <CustomButton
            type="link"
            disabled={group.timeSlots.length >= 3}
            onClick={() => addTimeSlot(group.id)}
          >
            <PlusOutlined style={{ color: "#0D9A62" }} />
            Thêm khung giờ
          </CustomButton>
        </CustomCard>
      ))}

      <Button icon={<PlusOutlined />} style={{ marginBottom: 16 }} onClick={addDayGroup} disabled={selectedDays.size >= days.length} >
        Thêm ngày
      </Button>
    </div>
  );
};

export default BusinessHours;
